import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MapPin, ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Menu, X, Sparkles,
  Brain, Users, Gift, Video, ShieldCheck, UserPlus, Building2, Handshake, TrendingUp,
} from 'lucide-react';
import JobastiLogo from '../components/JobastiLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BLUE = '#2563EB';
const BLUE_DEEP = '#1D4ED8';
const HERO_BG = 'linear-gradient(180deg, #2E6BF2 0%, #2563EB 48%, #1D4ED8 100%)';

const Brand = () => (
  <span className="notranslate" translate="no">
    <span className="text-navy-900">Jobasti</span><span className="text-blue-600">IQ</span>
  </span>
);

const CARD_GRADIENTS = [
  `radial-gradient(circle at 72% 42%, rgba(37,99,235,0.95) 0%, rgba(0,180,255,0.7) 28%, transparent 60%),
   radial-gradient(circle at 25% 68%, rgba(0,210,255,0.85) 0%, transparent 55%), #06133a`,
  `radial-gradient(circle at 70% 40%, rgba(99,102,241,0.95) 0%, rgba(129,140,248,0.7) 28%, transparent 60%),
   radial-gradient(circle at 26% 66%, rgba(76,29,149,0.85) 0%, transparent 55%), #0f0a2e`,
  `radial-gradient(circle at 70% 42%, rgba(16,185,129,0.9) 0%, rgba(45,212,191,0.7) 28%, transparent 60%),
   radial-gradient(circle at 26% 66%, rgba(5,150,120,0.85) 0%, transparent 55%), #04211a`,
  `radial-gradient(circle at 70% 40%, rgba(255,77,140,0.95) 0%, rgba(255,120,170,0.7) 28%, transparent 60%),
   radial-gradient(circle at 28% 66%, rgba(190,60,120,0.85) 0%, transparent 55%), #2a0a1c`,
  `radial-gradient(circle at 70% 42%, rgba(245,158,11,0.9) 0%, rgba(251,191,36,0.7) 28%, transparent 60%),
   radial-gradient(circle at 26% 66%, rgba(217,119,6,0.85) 0%, transparent 55%), #2a1704`,
];

const POPULAR_SEARCH_KEYS = ['IT', 'Marketing', 'Sales', 'Project Management', 'Home office', 'Full-time'];

const Eyebrow = ({ label, center = false }) => (
  <div className={`flex items-center gap-4 mb-4 ${center ? 'justify-center' : ''}`}>
    <div className="h-[1px] w-12 bg-blue-400"></div>
    <span className="text-blue-500 font-bold tracking-[0.2em] text-xs uppercase">{label}</span>
    {center && <div className="h-[1px] w-12 bg-blue-400"></div>}
  </div>
);

const JobseekerLanding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, userProfile } = useAuth();
  const [search, setSearch] = useState('');
  const [locSearch, setLocSearch] = useState('');
  const [tIdx, setTIdx] = useState(1);
  const [openFaq, setOpenFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const USPS = [
    { icon: Brain, title: t('seekerUsp.u1t'), desc: t('seekerUsp.u1d') },
    { icon: Users, title: t('seekerUsp.u2t'), desc: t('seekerUsp.u2d') },
    { icon: Gift, title: t('seekerUsp.u3t'), desc: t('seekerUsp.u3d') },
    { icon: Video, title: t('seekerUsp.u4t'), desc: t('seekerUsp.u4d') },
    { icon: ShieldCheck, title: t('seekerUsp.u5t'), desc: t('seekerUsp.u5d') },
  ];

  const STEPS = [
    { icon: UserPlus, title: t('seekerHow.s1t'), desc: t('seekerHow.s1d') },
    { icon: Sparkles, title: t('seekerHow.s2t'), desc: t('seekerHow.s2d') },
    { icon: Video, title: t('seekerHow.s3t'), desc: t('seekerHow.s3d') },
    { icon: Building2, title: t('seekerHow.s4t'), desc: t('seekerHow.s4d') },
    { icon: Handshake, title: t('seekerHow.s5t'), desc: t('seekerHow.s5d') },
  ];

  const TESTIMONIALS = [
    { name: 'Christian', img: 'https://i.pravatar.cc/100?img=12', text: t('seekerTestimonials.t1') },
    { name: 'Larissa', img: 'https://i.pravatar.cc/100?img=47', text: t('seekerTestimonials.t2') },
    { name: 'Dominik', img: 'https://i.pravatar.cc/100?img=33', text: t('seekerTestimonials.t3') },
  ];

  const FAQS = [1, 2, 3, 4, 5, 6].map((n) => ({ q: t(`seekerFaq.q${n}`), a: t(`seekerFaq.a${n}`) }));

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      if (userProfile.role === 'jobasti_admin') navigate('/admin/dashboard', { replace: true });
      else if (['client_admin', 'client_recruiter'].includes(userProfile.role)) navigate('/dashboard', { replace: true });
      else navigate('/jobs', { replace: true });
    }
  }, [isAuthenticated, userProfile, navigate]);

  const goSearch = (q, loc) => {
    const usp = new URLSearchParams();
    const term = (q ?? search).trim();
    const where = (loc ?? locSearch).trim();
    if (term) usp.set('q', term);
    if (where) usp.set('loc', where);
    const qs = usp.toString();
    navigate(qs ? `/jobs?${qs}` : '/jobs');
  };

  return (
    <div className="antialiased text-slate-800 bg-white selection:bg-blue-200 selection:text-blue-900" style={{ fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif" }} data-testid="jobseeker-landing">
      {/* ── NAV (for-hire style) ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
          <Link to="/for-jobseekers" className="flex items-center gap-3" data-testid="ls-logo">
            <img src="/brand-flame.png" alt="JobastiIQ" className="h-10 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-9 lg:gap-10 text-[17px] text-slate-700 font-medium">
            <button onClick={() => goSearch()} className="hover:text-slate-900 transition-colors">{t('seekerNav.jobSearch')}</button>
            <a href="mailto:support@jobasti.com" className="hover:text-slate-900 transition-colors">{t('seekerNav.helpCenter')}</a>
            <Link to="/" className="hover:text-slate-900 transition-colors">{t('seekerNav.forCompanies')}</Link>
          </nav>
          <div className="flex items-center gap-3 lg:gap-4">
            <LanguageSwitcher />
            <Link
              to="/login"
              onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'jobseeker'); } catch { /* noop */ } }}
              className="hidden md:inline-flex items-center rounded-full border border-slate-300 px-6 py-2.5 text-[15px] font-semibold text-slate-800 hover:border-slate-400 hover:bg-slate-50 transition-colors"
              data-testid="ls-login-btn"
            >
              {t('common.login')}
            </Link>
            <Link to="/prospect-register" className="hidden md:inline-flex items-center text-white px-6 py-2.5 rounded-full text-[15px] font-semibold transition-opacity hover:opacity-90" style={{ backgroundColor: BLUE }} data-testid="ls-signup-btn">
              {t('seekerCta.btn')}
            </Link>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-slate-800 hover:bg-slate-100 transition-colors"
              aria-label="Menu"
              aria-expanded={menuOpen}
              data-testid="ls-mobile-menu-btn"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="ls-mobile-menu">
            <div className="px-6 py-4 flex flex-col">
              <button onClick={() => { goSearch(); setMenuOpen(false); }} className="text-left py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('seekerNav.jobSearch')}</button>
              <Link to="/" onClick={() => setMenuOpen(false)} className="py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('seekerNav.forCompanies')}</Link>
              <div className="flex flex-col gap-3 pt-5 pb-2">
                <Link to="/login" onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'jobseeker'); } catch { /* noop */ } setMenuOpen(false); }} className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-[15px] font-semibold text-slate-800">{t('common.login')}</Link>
                <Link to="/prospect-register" onClick={() => setMenuOpen(false)} className="inline-flex items-center justify-center text-white px-6 py-3 rounded-full text-[15px] font-semibold" style={{ backgroundColor: BLUE }}>{t('seekerCta.btn')}</Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO (blue background + people photo + job search) ───────────── */}
      <section className="relative text-white pt-16 pb-20 lg:pt-20 lg:pb-24 overflow-hidden" style={{ background: HERO_BG }} data-testid="ls-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-[1px] w-10 bg-blue-300/70"></div>
              <span className="text-blue-100 font-semibold tracking-[0.28em] text-xs uppercase">{t('seekerHero.eyebrow')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] font-serif font-medium leading-[1.05] text-white" data-testid="ls-hero-title">
              {t('seekerHero.title1')}{' '}
              <span className="italic font-bold text-blue-200">{t('seekerHero.title2')}</span>
            </h1>
            <p className="mt-6 text-lg text-blue-50/90 max-w-lg leading-relaxed">{t('seekerHero.sub')}</p>

            <form
              onSubmit={(e) => { e.preventDefault(); goSearch(); }}
              className="mt-9 flex flex-col sm:flex-row items-stretch bg-white rounded-2xl sm:rounded-full shadow-xl shadow-blue-900/10 ring-1 ring-slate-200/80 overflow-hidden"
              data-testid="ls-search-form"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-4">
                <Search className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('seekerHero.searchPlaceholder')} className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0" data-testid="ls-search-input" />
              </div>
              <div className="hidden sm:block w-px bg-slate-200 my-3" />
              <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-4 border-t border-slate-100 sm:border-t-0">
                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <input value={locSearch} onChange={(e) => setLocSearch(e.target.value)} placeholder={t('seekerHero.locPlaceholder')} className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0" data-testid="ls-search-loc" />
              </div>
              <button type="submit" className="text-white font-semibold text-sm px-8 py-4 sm:m-2 rounded-full transition-opacity hover:opacity-90" style={{ backgroundColor: BLUE }} data-testid="ls-search-submit">
                {t('seekerHero.findJobs')}
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="text-xs text-blue-100/70 font-medium">{t('seekerNav.jobSearch')}:</span>
              {POPULAR_SEARCH_KEYS.map((k) => (
                <button key={k} onClick={() => goSearch(k)} className="text-xs px-3 py-1.5 rounded-full border border-white/30 text-blue-50 hover:border-white/60 hover:bg-white/10 transition-colors" data-testid={`ls-popular-${k}`}>
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-slate-200/60 aspect-[4/5] max-w-md mx-auto">
              <img
                src="https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&auto=format&fit=crop"
                alt="Happy professionals celebrating together"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute z-20 top-8 -right-2 sm:-right-4 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-100 px-4 py-3 shadow-xl notranslate" translate="no">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-navy-900 text-sm font-extrabold">96%</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">AI Match Score</p>
            </div>
            <div className="absolute z-20 bottom-10 -left-3 sm:-left-5 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-100 px-4 py-3 shadow-xl notranslate" translate="no">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-navy-900 text-xs font-semibold">New match · Product Manager</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5 pl-4">Berlin · Full-time · Remote friendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── USP → blue cards grid (rows of 3) ────────────────────────────── */}
      <section className="bg-white py-24 lg:py-28" data-testid="ls-usp-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Eyebrow label={t('seekerNav.forTalent', 'why us')} />
          <h2 className="text-4xl lg:text-5xl font-serif font-medium text-navy-900 mb-4">
            {t('seekerUsp.t1')} <Brand /> {t('seekerUsp.t2')}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mb-12 leading-relaxed">{t('seekerHero.sub')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {USPS.map((u, i) => (
              <div
                key={u.title}
                onClick={() => navigate('/prospect-register')}
                className="rounded-3xl p-4 sm:p-5 border border-white/[0.08] transition-all duration-300 flex flex-col justify-between group cursor-pointer shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:brightness-110"
                style={{ backgroundColor: BLUE }}
                data-testid={`ls-usp-card-${i}`}
              >
                <div>
                  <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden relative flex items-center justify-center select-none shadow-inner border border-white/10">
                    <div className="absolute inset-0 group-hover:scale-105 transition-transform duration-700 ease-out" style={{ background: CARD_GRADIENTS[i % CARD_GRADIENTS.length], filter: 'blur(16px) contrast(1.15)' }} />
                    <div className="absolute inset-0 bg-black/20" />
                    <span className="relative z-10 font-serif text-xl sm:text-2xl font-medium tracking-tight text-white drop-shadow-lg text-center px-4">{u.title}</span>
                  </div>
                  <h3 className="mt-5 text-lg sm:text-[19px] font-medium text-white leading-snug tracking-tight group-hover:text-blue-50 transition-colors">{u.desc}</h3>
                </div>
                <div className="mt-8 pt-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-50 group-hover:text-white transition-colors">{t('seekerSupport.cta')}</span>
                  <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/15 flex items-center justify-center text-white group-hover:bg-white group-hover:text-blue-600 transition-all duration-300">
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section id="how" className="py-24 bg-gray-50" data-testid="ls-how-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Eyebrow label={t('seekerHow.t1')} center />
          <h2 className="text-4xl lg:text-5xl font-serif font-medium text-center text-navy-900">
            {t('seekerHow.t1')} <Brand /> {t('seekerHow.t2')}
          </h2>
          <div className="mt-16 flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-0">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.title}>
                {i > 0 && <div className="hidden lg:block flex-1 border-t border-dashed border-slate-300 mt-10 mx-2" />}
                <div className="flex flex-col items-center text-center w-52 flex-shrink-0">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: i % 2 === 0 ? BLUE_DEEP : BLUE }}>
                      <s.icon className="w-8 h-8 text-white" strokeWidth={1.6} />
                    </div>
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border border-slate-200 text-blue-600 text-xs font-bold flex items-center justify-center shadow">{i + 1}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-lg text-navy-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUPPORT (editorial two-column, people photo) ─────────────────── */}
      <section className="bg-white py-24" data-testid="ls-support-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-slate-200/60 aspect-[5/4]">
              <img
                src="https://images.unsplash.com/photo-1698047682091-782b1e5c6536?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&auto=format&fit=crop"
                alt="Candidate meeting a recruiter"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow label={t('seekerSupport.title1')} />
            <h2 className="text-4xl lg:text-5xl font-serif font-medium leading-[1.1] text-navy-900">
              {t('seekerSupport.title1')} <span className="italic text-blue-600">{t('seekerSupport.title2')}</span>
            </h2>
            <p className="text-gray-600 mt-6 leading-relaxed text-lg max-w-md">{t('seekerSupport.body')}</p>
            <button
              onClick={() => navigate('/prospect-register')}
              className="mt-8 inline-flex items-center gap-2 text-white text-base font-bold rounded-full px-8 py-3.5 shadow-lg transition-all hover:opacity-90 group"
              style={{ backgroundColor: BLUE_DEEP }}
              data-testid="ls-find-jobs-cta"
            >
              {t('seekerSupport.cta')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-24" data-testid="ls-testimonials">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <Eyebrow label={t('seekerTestimonials.title2')} center />
          <h2 className="text-4xl lg:text-5xl font-serif font-medium text-navy-900">
            {t('seekerTestimonials.title1')} <span className="italic text-blue-600">{t('seekerTestimonials.title2')}</span>
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
            {TESTIMONIALS.map((testi, i) => (
              <div key={i} className={`rounded-3xl p-8 text-left border transition-all duration-300 ${i === tIdx ? 'shadow-xl border-blue-200 bg-white -translate-y-1' : 'shadow-sm border-slate-100 bg-white'}`}>
                <p className="text-gray-700 leading-relaxed">{testi.text}</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={testi.img} alt={testi.name} className="w-11 h-11 rounded-full object-cover" />
                  <p className="text-sm text-navy-900 font-semibold">{testi.name}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center gap-4">
            <button onClick={() => setTIdx((i) => (i + TESTIMONIALS.length - 1) % TESTIMONIALS.length)} className="w-11 h-11 rounded-full border border-slate-300 flex items-center justify-center text-slate-700 hover:bg-white hover:border-slate-400 transition-colors" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => setTIdx((i) => (i + 1) % TESTIMONIALS.length)} className="w-11 h-11 rounded-full border border-slate-300 flex items-center justify-center text-slate-700 hover:bg-white hover:border-slate-400 transition-colors" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white py-24" data-testid="ls-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <Eyebrow label={t('seekerFaq.title2')} center />
          <h2 className="text-4xl lg:text-5xl font-serif font-medium text-center text-navy-900">
            {t('seekerFaq.title1')} <span className="italic text-blue-600">{t('seekerFaq.title2')}</span>
          </h2>
          <div className="mt-12 divide-y divide-slate-200" data-testid="ls-faq-list">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button type="button" onClick={() => setOpenFaq(open ? -1 : i)} className="w-full flex items-center gap-4 py-5 text-left group" data-testid={`ls-faq-q-${i}`} aria-expanded={open}>
                    <ChevronDown className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                    <span className="flex-1 text-navy-900 font-semibold text-base group-hover:text-blue-600 transition-colors">{f.q}</span>
                  </button>
                  {open && (
                    <div className="pb-6 pl-9 pr-2 text-sm text-gray-700 leading-relaxed" data-testid={`ls-faq-a-${i}`}>{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA + FOOTER (for-hire style) ────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: BLUE_DEEP }} data-testid="ls-cta-footer">
        <div className="absolute top-0 right-0 h-full w-1/2 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-blue-400/10 blur-3xl"></div>
        </div>
        <div className="container mx-auto px-6 py-24 lg:py-28 relative z-10 flex flex-col items-center text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-white max-w-2xl leading-[1.08]">{t('seekerCta.title')}</h2>
          <p className="mt-4 text-blue-100 text-lg max-w-xl">{t('seekerCta.sub')}</p>
          <button
            onClick={() => navigate('/prospect-register')}
            className="mt-10 inline-flex items-center gap-2 bg-white px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-colors group"
            style={{ color: BLUE_DEEP }}
            data-testid="ls-get-started-cta"
          >
            {t('seekerCta.btn')}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" style={{ color: BLUE }} />
          </button>
        </div>

        <div className="border-t border-white/10 relative z-10">
          <div className="container mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-blue-100/70">
            <div className="flex items-center gap-3">
              <JobastiLogo mono height={26} />
              <span>&copy; {new Date().getFullYear()} JobastiIQ</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-white transition-colors">{t('seekerFooter.forCompanies')}</Link>
              <Link to="/login" onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'jobseeker'); } catch { /* noop */ } }} className="hover:text-white transition-colors">{t('seekerFooter.login')}</Link>
              <a href="mailto:hello@jobasti.com" className="hover:text-white transition-colors">hello@jobasti.com</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobseekerLanding;
